interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface HomeSEOProps {
  type: 'home';
}

interface SearchSEOProps {
  type: 'search';
  query?: string;
  resultCount?: number;
  breadcrumbItems: BreadcrumbItem[];
}

interface FAQItem {
  question: string;
  answer: string;
}

interface PageSEOProps {
  type: 'home' | 'search';
  query?: string;
  resultCount?: number;
  breadcrumbItems: BreadcrumbItem[];
  faqItems?: FAQItem[];
}

function buildBreadcrumbLD(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org' as const,
    '@type': 'BreadcrumbList' as const,
    itemListElement: items.map((item, idx) => ({
      '@type': 'ListItem' as const,
      position: idx + 1,
      name: item.label,
      ...(item.to ? { item: `http://xudongprc.com/${item.to}` } : {}),
    })),
  };
}

function getOrganizationLD() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '古风漫画网',
    url: 'http://xudongprc.com/',
    description: '古风漫画网是国内领先的韩国漫画在线阅读平台，致力于为中国读者提供海量优质的韩国漫画资源，以独特的古风审美视角甄选每一部作品。',
    foundingDate: '2024-03-01',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-6666',
      contactType: 'customer service',
      email: 'hello@xudongprc.com',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '文三路478号华星时代广场15层',
      addressLocality: '杭州市',
      addressRegion: '浙江省',
      addressCountry: 'CN',
      postalCode: '310000',
    },
  };
}

function getWebsiteLD() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '古风漫画网',
    alternateName: 'GufengManhua',
    url: 'http://xudongprc.com/',
    description: '免费韩国漫画在线阅读平台，热门韩漫下拉式观看，以古风之美诠释韩漫新境界。',
    inLanguage: 'zh-CN',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'http://xudongprc.com/search?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

function getFAQPageLD(items: FAQItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export default function PageSEO({ type, query, resultCount, breadcrumbItems, faqItems }: PageSEOProps) {
  const breadcrumbLD = buildBreadcrumbLD(breadcrumbItems);
  const orgLD = getOrganizationLD();

  const schemas: object[] = [breadcrumbLD];

  if (type === 'home') {
    schemas.push(orgLD);
    schemas.push(getWebsiteLD());
    if (faqItems && faqItems.length > 0) {
      schemas.push(getFAQPageLD(faqItems));
    }
  }

  if (type === 'search') {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: query ? `搜索: ${query} - 古风漫画网` : '搜索漫画 - 古风漫画网',
      description: query
        ? `搜索"${query}"，共找到${resultCount || 0}个相关漫画作品`
        : '搜索古风漫画网海量韩国漫画资源，按名称、作者、分类快速找到你想看的漫画。',
      url: query
        ? `http://xudongprc.com/search?q=${encodeURIComponent(query)}`
        : 'http://xudongprc.com/search',
      isPartOf: {
        '@type': 'WebSite',
        name: '古风漫画网',
        url: 'http://xudongprc.com/',
      },
    });
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schemas) }}
    />
  );
}